import type { TranslationShape } from "./ru.js";
import { ru } from "./ru.js";

export const en = {
  app: {
    name: "Secure Mind",
    loading: "Loading",
    loadingSecurely: "Checking session",
    stale: "Refreshing",
    accessRemoved: "Access removed",
  },
  nav: {
    focus: "Focus",
    work: "Work",
    graph: "Connections",
    documents: "Documents",
  },
  common: {
    create: "Create",
    save: "Save",
    cancel: "Cancel",
    retry: "Retry",
    copy: "Copy",
    close: "Close",
    search: "Search",
    accept: "Accept",
    reject: "Reject",
    move: "Move",
  },
  auth: {
    common: {
      working: "Please wait…",
    },
    errors: {
      generic: "The action could not be completed",
      invalidLink: "This link is invalid, expired, or already used",
      passwordMismatch: "Passwords do not match",
      passkeyUnavailable: "This browser does not support passkeys",
    },
    signIn: {
      title: "Sign in to Secure Mind",
      description: "A personal workspace with protected access",
      passkey: "Sign in with a passkey",
      passkeyHelp: "Confirm sign-in on this device or an external security key",
      passwordDivider: "or sign in with a password",
      email: "Email",
      password: "Password",
      passwordSubmit: "Sign in",
      totpTitle: "Verification code",
      totpHelp: "Enter the six-digit code from your authenticator app",
      totpCode: "One-time code",
      verifyTotp: "Verify code",
      useBackupCode: "Use a backup code",
      backupTitle: "Backup code",
      backupHelp: "Enter one of your saved one-time backup codes",
      backupCode: "Backup code",
      verifyBackup: "Verify backup code",
      useTotp: "Use an authenticator code",
    },
    setup: {
      title: "Owner setup",
      loading: "Checking the setup link",
      description: "Create a protected sign-in method. Public registration is not available.",
      passkeyTitle: "Passkey",
      passkeyHelp: "Recommended: confirm with your device instead of a password",
      createPasskey: "Create passkey",
      passwordTitle: "Password and TOTP",
      passwordHelp: "Use a password of at least 12 characters and an authenticator app",
      password: "Password",
      confirmPassword: "Confirm password",
      continuePassword: "Continue with password",
      totpTitle: "Connect an authenticator app",
      totpHelp: "Scan the code locally or add the URI manually, then enter the six-digit code",
      totpCode: "Authenticator code",
      verifyTotp: "Finish setup",
      backupCodesTitle: "Save your backup codes",
      backupCodesHelp: "These codes are shown once. Store them separately from this device.",
      retryTotp: "Request new TOTP options",
      expired: "The setup link has expired. Create a new link on the server.",
    },
    simulation: {
      title: "Simulation mode",
      loading: "Checking the one-time link",
      description: "Secure Mind will open with a restricted identity and allowed workspaces",
      ready: "Simulation is ready",
      conflictTitle: "Another identity is already active",
      conflictHelp: "End the current sign-in or simulation before using this link",
      return: "Return",
    },
  },
  identity: {
    simulation: "{{mock}} via {{actor}}",
    simulationNotice: "You are working in a simulation. Changes are not real.",
    stopSimulation: "Stop simulation",
    exitSimulation: "Exit simulation",
    recoveryTitle: "Two active identities were detected",
  },
  readiness: {
    unpaired: "Not connected",
    noGrant: "Access not granted",
    offline: "Offline",
    modelUnavailable: "Assistant unavailable",
    ready: "Ready",
    unavailable: "Unavailable",
  },
  shell: {
    primaryNavigation: "Main navigation",
    hideNavigation: "Hide navigation",
    showNavigation: "Show navigation",
    workspace: "Workspace",
    noWorkspace: "No workspace",
    settings: "Settings",
    openSettings: "Account and settings",
    accountAndWorkspace: "Account and workspace",
    account: "Account",
    effectiveAccount: "Signed-in account",
    actorAccount: "Acting owner",
    readiness: "Assistant access",
    modelObserved: "{{model}}",
    preferences: "Preferences",
    interfaceLanguage: "Language",
    changeLanguage: "Change language",
    locale: {
      ru: "Русский",
      en: "English",
    },
    reducedMotion: "Reduce motion",
    reducedMotionHint: "Uses system and account settings",
    simulationMode: "Simulation mode",
    simulationPreferences: "Account settings unavailable in simulation",
    signOut: "Sign out",
  },
  workspaceGate: {
    title: "Create your workspace",
    description: "Your account is ready. Create a workspace to keep chats, tasks, connections, and documents together.",
    nameLabel: "Workspace name",
    namePlaceholder: "For example, Product team",
    create: "Create workspace",
    creating: "Creating workspace…",
    simulationTitle: "This simulation has no workspace",
    simulationDescription: "Workspace creation is unavailable for this simulation. Exit simulation to return to your account.",
    exitSimulation: "Exit simulation",
    exitingSimulation: "Exiting simulation…",
  },
  commandSearch: {
    eyebrow: "Workspace",
    title: "Search or command",
    trigger: "Search or command",
    inputLabel: "Search workspace",
    placeholder: "Search documents, tasks, chats…",
    destinations: "Go to",
    commandDescriptions: {
      focus: "Start a new chat",
      work: "Review projects and tasks",
      graph: "Inspect workspace connections",
      documents: "Browse documents",
    },
    loading: "Searching…",
    noResults: "No matches for “{{query}}”.",
    error: "Workspace search is unavailable.",
    archived: "Archived",
    types: {
      note: "Note",
      project: "Project",
      task: "Task",
      document: "Document",
      focus_thread: "Focus chat",
      research_source: "Research source",
      account: "Account",
    },
  },
  focus: {
    title: "Focus",
    threads: "Chats",
    notes: "Notes",
    inbox: "Inbox",
    empty: "Nothing here yet",
    compose: "Message",
    organize: "Organize",
    openInbox: "Open inbox",
    jumpLatest: "Jump to latest",
    sourceObserved: "Source used",
    awaitingQuery: "Review search queries",
    noLeaseHeld: "No assistant connection",
    newThread: "New chat",
    newThreadAction: "New chat",
    showConnections: "Show in Connections",
    untitled: "New chat",
    emptyEyebrow: "New chat",
    emptyHeading: "What do you want to do?",
    emptyDescription: "Ask a question or describe a task.",
    suggestionsLabel: "Examples",
    suggestions: {
      plan: "Plan my task",
      compare: "Compare options and risks",
      summarize: "Summarize and list next steps",
    },
    suggestionUse: "Use example",
    noWorkspace: "Choose a workspace.",
    history: "Chat history",
    historyAction: "History",
    closeHistory: "Close",
    searchHistory: "Search chats",
    searchHistoryPlaceholder: "Find a chat…",
    search: "Search",
    clearSearch: "Clear",
    historyEmpty: "No chats found.",
    historyNoResults: "No chats match this search.",
    historyLoading: "Loading chat history…",
    historyError: "Chat history is unavailable.",
    moreHistory: "Show more",
    branch: "Branch",
    threadDocument: "Messages",
    threadLoading: "Loading chat…",
    threadError: "This chat could not be loaded.",
    threadErrorDescription: "The chat may have moved or the connection was interrupted.",
    threadNotFound: "Chat not found",
    threadNotFoundDescription: "This chat may have been deleted or is no longer in this workspace.",
    backToHistory: "Back to chat history",
    messagesLoading: "Loading messages…",
    messagesError: "Messages could not be loaded.",
    retryThread: "Retry chat",
    retryMessages: "Retry messages",
    threadEmpty: "No messages yet.",
    loadOlder: "Load earlier messages",
    loadingOlder: "Loading…",
    assistant: "Assistant",
    you: "You",
    currentRun: "Current reply",
    runStatus: "Status: {{state}}",
    cancel: "Cancel",
    retry: "Retry",
    pollDelayed: "Updates delayed; reconnecting.",
    publicSearch: "Allow web search",
    publicSearchDescription: "Searches are reviewed before they run. Off by default.",
    context: "Add context · {{count}}",
    removeContext: "Remove {{label}}",
    contextEmpty: "Nothing to add yet.",
    contextLoading: "Loading attachments…",
    contextUnavailable: "Attachments are unavailable.",
    contextMessage: "Message in this chat",
    uploadToDocuments: "Upload to Documents",
    openDocuments: "Open Documents",
    uploadPending: "Upload is still being checked. Remove it or wait until it is available in Focus.",
    contextReasons: {
      uploading: "Uploading",
      security: "Security check",
      unsafe: "Unsafe",
      unsupported: "Not supported in Focus",
      blocked: "AI access blocked",
    },
    composePlaceholder: "Ask a question or describe a task…",
    send: "Send",
    sending: "Sending…",
    copy: "Copy",
    copied: "Copied",
    copyFailed: "Could not copy",
    queryReview: "Review internet searches",
    queryReviewDescription: "Approve or edit searches before they run.",
    queryNumber: "Search {{number}}",
    approve: "Approve",
    saveQueries: "Save edits",
    reject: "Reject",
    sources: "Sources",
    sourceLoading: "Loading source…",
    reviewProposal: "Review proposed change",
    proposalLoading: "Loading change…",
    proposalOperation: "Action",
    proposalState: "Status",
    proposalOperations: {
      noteCreate: "Create note",
      noteUpdate: "Update note",
      noteArchive: "Archive note",
      projectCreate: "Create project",
      projectUpdate: "Update project",
      taskCreate: "Create task",
      linkCreate: "Create connection",
      linkRemove: "Remove connection",
      documentPatch: "Edit document",
      documentReplace: "Replace document",
      unknown: "Proposed change",
    },
    proposalStates: {
      pending: "Awaiting review",
      blocked_by_edit_session: "Blocked by an active editor",
      applying: "Applying",
      accepted: "Accepted",
      rejected: "Rejected",
      expired: "Expired",
      conflict: "Conflict",
      failed: "Failed",
      unknown: "Unknown",
    },
    acceptProposal: "Accept change",
    rejectProposal: "Reject change",
    requestFailed: "Action failed. Refresh and try again.",
    startFailed: "The request could not be started.",
    partialStartFailed: "The chat was created, but the request did not start.",
    retryStart: "Retry request",
    announceQueued: "Request queued.",
    announceQueryReview: "Internet searches need review.",
    announceCancelling: "Cancelling request.",
    announceConflict: "State changed. Refreshing before retry.",
    announceState: "Status: {{state}}.",
    announceTerminal: "Finished: {{state}}.",
    readiness: {
      unpaired: "Not connected",
      no_grant: "Access not granted",
      offline: "Offline",
      model_unavailable: "Assistant unavailable",
      ready: "Ready",
    },
    states: {
      queued: "queued",
      planning: "planning",
      awaiting_query_review: "awaiting review",
      searching: "searching internet",
      synthesizing: "writing reply",
      cancelling: "cancelling",
      cancelled: "cancelled",
      failed: "failed",
      interrupted: "interrupted",
      succeeded: "done",
    },
  },
  work: {
    title: "Work",
    eyebrow: "Tasks",
    pageDescription: "Plan and move tasks.",
    createProject: "New project",
    projectName: "Project name",
    timezoneLabel: "Time zone",
    noWorkspace: "No workspace",
    noProjects: "No active projects",
    noProjectsTitle: "No projects yet",
    noProjectsDescription: "Create the first project to give this workspace a place for tasks.",
    noProjectsReadOnly: "This workspace has no projects yet. A workspace editor can create the first project.",
    noTasks: "No tasks here yet",
    noTasksForProjectTitle: "No tasks in this project",
    noTasksForProjectDescription: "Add the first task to start moving work through the board.",
    noTasksForProjectReadOnly: "This project has no tasks yet. A workspace editor can add the first task.",
    noMyTasksTitle: "No tasks assigned to you",
    noMyTasksDescription: "Choose a project or view all project tasks to find work to pick up.",
    viewProjectTasks: "View project tasks",
    noFilteredTasksTitle: "No tasks match these filters",
    noFilteredTasksDescription: "Try a different status, title, or assignment filter.",
    clearFilters: "Clear filters",
    noDeadline: "No due date",
    archivedState: "Archived task",
    loading: "Loading tasks…",
    board: "Task board",
    project: "Project",
    chooseProject: "Choose a project",
    allProjects: "All projects",
    myTasks: "My tasks",
    boardView: "Board",
    listView: "List",
    filter: "Filter tasks",
    filterPlaceholder: "Task title",
    statusFilter: "Status",
    allStatuses: "All statuses",
    quickCreate: "Add task",
    quickCreatePlaceholder: "Add a task…",
    taskTitle: "Task title",
    status: "Status",
    dropHere: "Drop here",
    drag: "Move",
    dragHandle: "Move task",
    addTask: "Add task",
    move: "Move",
    moveTo: "Move to",
    assignees: "Assignees: {{count}}",
    assignedToMe: "Assigned to me",
    comments: "Comments: {{count}}",
    taskDetail: "Task details",
    description: "Description",
    priority: "Priority",
    deadline: "Due date",
    assigneesInput: "Assignees, separated by commas",
    assigneesPlaceholder: "Choose accounts",
    labelsInput: "Labels, separated by commas",
    labelsPlaceholder: "for example, research, important",
    descriptionPreview: "Description",
    commentsAndActivity: "Comments and activity",
    commentPlaceholder: "Add a comment",
    addComment: "Add comment",
    loadMore: "Load more",
    loadingMoreTasks: "Loading more tasks…",
    loadMoreTasks: "Load more tasks",
    incompleteResults: "More tasks are available.",
    archive: "Archive",
    restore: "Restore",
    moveSaved: "Task moved",
    moveConflict: "The board changed. Latest data was restored.",
    error: "Action failed",
    showInConnections: "Show in Connections",
    detailStates: {
      saved: "All changes saved.",
      unsaved: "Unsaved changes.",
      saving: "Saving changes…",
      stale: "This task changed elsewhere. Reload the latest task before retrying.",
      forbidden: "View only. You do not have permission to change this task.",
    },
    loadMoreBeforeMove: "Load all project tasks before moving this task.",
    readOnly: "View only. Editing and comments are disabled.",
    statuses: {
      inbox: "Inbox",
      planned: "Planned",
      inProgress: "In progress",
      review: "Review",
      done: "Done",
    },
    priorities: {
      urgent: "Urgent",
      high: "High",
      medium: "Medium",
      low: "Low",
      none: "No priority",
    },
    activity: {
      created: "Created",
      updated: "Updated",
      ranked: "Moved",
      commented: "Comment added",
      archived: "Archived",
      restored: "Restored",
    },
  },
  graph: {
    title: "Connections",
    chooseRoot: "Find an item",
    rootResults: "Results",
    loaded: "Showing {{loaded}} of {{available}}",
    truncated: "Some connections are hidden.",
    semanticView: "Items and connections",
    nodes: "Items",
    edges: "Connections",
    node: "Item",
    edge: "Connection",
    actions: "Actions",
    expand: "Show more",
    revisionLabel: "Version",
    revision: "Version {{revision}}",
    noWorkspace: "No workspace",
    noResults: "No matches for “{{query}}”",
    relationshipEditor: "Add connection",
    description: "Find how items connect.",
    searchPlaceholder: "Search by title",
    searchHelp: "Enter a title to begin.",
    startEyebrow: "Start here",
    startTitle: "Find an item to begin",
    startDescription: "Choose an item, then follow its connections.",
    startSearchTitle: "Find",
    startSearchDescription: "Search by title.",
    startExploreTitle: "Follow",
    startExploreDescription: "Open its connections.",
    startInspectTitle: "Inspect",
    openWork: "Open Work",
    openDocuments: "Open Documents",
    openFocus: "Open Focus",
    tryExample: "Try an example",
    exampleEyebrow: "Product tour",
    exampleTitle: "Example graph",
    exampleDescription: "Select an item to inspect it, or show connections to follow another path.",
    exampleNotice: "Example data — not saved to this workspace.",
    exampleScope: "{{count}} example items · no workspace data changed",
    returnToWorkspace: "Return to workspace",
    exampleProject: "Example project",
    exampleTask: "Example task",
    exampleDocument: "Example document",
    exampleFocusThread: "Example Focus thread",
    exampleAccount: "Example account",
    startInspectDescription: "Select an item for details.",
    search: "Search",
    searching: "Searching…",
    loadMoreRoots: "More results",
    mapView: "Map",
    showConnections: "Show connections",
    showConnectionsFor: "Show connections for",
    listView: "List",
    viewLabel: "View",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    fitGraph: "Fit graph",
    filters: "Filters",
    projectId: "Project",
    accountId: "Account",
    tag: "Tag",
    includeArchived: "Include archived",
    applyFilters: "Apply",
    clearFilters: "Clear",
    root: "Starting item",
    selectedNode: "Selected item",
    selectedEdge: "Selected connection",
    open: "Open",
    openIn: {
      work: "Open in Work",
      documents: "Open in Documents",
      focus: "Open in Focus",
    },
    select: "Select",
    noSelection: "Select an item or connection.",
    nodeType: "Type",
    edgeType: "Connection type",
    source: "From",
    target: "To",
    linkType: "Connection type",
    createLink: "Add connection",
    removeLink: "Remove connection",
    linkCreated: "Connection added",
    linkRemoved: "Connection removed",
    linkPending: "Saving connection…",
    linkSaved: "Connection saved.",
    linkStale: "The graph changed. Reload the connection and try again.",
    linkForbidden: "You no longer have permission to change connections.",
    linkError: "Connection could not be saved.",
    selfLink: "Choose two different items.",
    targetSearchPlaceholder: "Search authorized items",
    linkUnavailable: "Only editors and owners can manage connections.",
    noNodes: "No items found",
    noEdges: "No connections found",
    nextPage: "Next page",
    retry: "Try again",
    pageStatus: "Showing {{loaded}} of {{available}}",
    graphError: "Could not load connections.",
    filterHint: "Optional filters.",
    archived: "Archived",
    yes: "Yes",
    no: "No",
    nodeTypes: {
      note: "Note",
      project: "Project",
      task: "Task",
      document: "Document",
      focus_thread: "Focus chat",
      research_source: "Source",
      account: "Account",
    },
    edgeTypes: {
      references: "References",
      related_to: "Related",
      derived_from: "Comes from",
      supports: "Supports",
      assigned_to: "Assigned to",
    },
    rootSearchError: "Search failed.",
  },
  documents: {
    title: "Documents",
    eyebrow: "Files",
    pageDescription: "Verified files for Focus.",
    readyCount: "Verified: {{count}}",
    empty: "No documents yet",
    emptyDescription: "Uploads are checked before use.",
    noResults: "No matching documents",
    loading: "Loading documents…",
    search: "Search",
    searchPlaceholder: "Find a document…",
    filters: "Filters",
    all: "All",
    readyFilter: "Ready",
    scanningFilter: "Scanning",
    attentionFilter: "Needs attention",
    sort: "Sort",
    modifiedRecently: "Recently changed",
    upload: "Upload",
    uploadDocument: "Upload document",
    chooseFile: "Choose file",
    dropHint: "Drop a file or choose one",
    uploadProgress: "Uploaded {{percent}}% · security check",
    uploadComplete: "File sent for security check",
    uploadError: "The file could not be uploaded",
    refreshing: "Refreshing…",
    clearSearch: "Clear search",
    listError: "Documents could not be loaded",
    searchError: "Document search failed",
    errorDescription: "Your files are safe; try this operation again.",
    uploadQueue: {
      title: "Upload queue",
      count: "{{count}} file(s)",
      submitted: "Submitted as {{name}}",
      error: "Upload failed. Retry or remove this file.",
      open: "Open",
      retry: "Retry upload",
      cancel: "Cancel upload",
      remove: "Remove",
      states: {
        queued: "Queued",
        hashing: "Checking file hash",
        uploading: "Uploading",
        submitted: "Submitted",
        error: "Upload failed",
        canceled: "Canceled",
      },
    },
    fileType: "Type",
    state: "State",
    capability: "Focus access",
    version: "Version",
    modified: "Modified",
    size: "Size",
    open: "Open",
    download: "Download",
    history: "Version history",
    edit: "Edit",
    back: "Documents",
    selected: "File",
    metadata: "File details",
    currentVersion: "Current version",
    bytes: "Size",
    opening: "Opening",
    editing: "Editing",
    saving: "Saving",
    savedVersion: "Saved as version {{version}}",
    saveDraft: "Save draft",
    saveVersion: "Save version",
    draftSaved: "Draft saved",
    saveFailed: "Save failed",
    draftSaveFailed: "Draft could not be saved",
    versionSaveFailed: "Version could not be saved",
    outputActionFailed: "The edit output action could not be completed",
    saveConflict: "Save conflict",
    conflictTitle: "The document changed elsewhere",
    conflictDescription: "Your draft is safe. Compare before saving again.",
    compare: "Compare changes",
    reloadVersion: "Reload version",
    compareTitle: "Compare with current version",
    compareDescription: "Compare the draft with the current version before saving.",
    preview: "Preview",
    showConnections: "Show in Connections",
    downloadError: "Could not download this file",
    versionDownloadFailed: "Could not download this version",
    detailError: "Document detail is unavailable",
    detailErrorDescription: "The library is still available. Return to Documents or retry this detail.",
    downloadOnlyTitle: "Download-only file",
    downloadOnlyDescription: "This file has no safe in-browser viewer. Download it to use it.",
    viewerUnavailable: "The file is still in security review. Preview will be available when it is ready.",
    viewerError: "Preview failed",
    viewerErrorDescription: "The protected preview could not be loaded.",
    source: "Source",
    requirements: "File requirements",
    securityCheck: "Security check",
    proposal: "Suggested change",
    proposalText: "Check file size and integrity",
    proposalBasis: "For version {{version}}",
    removed: "Removed",
    added: "Added",
    acceptProposal: "Accept",
    rejectProposal: "Reject",
    stageOutput: "Review output",
    discardOutput: "Discard output",
    versionCurrent: "Current",
    versionSource: "Source",
    versionUpload: "Upload",
    scannerUnavailable: "Security check unavailable",
    unsafe: "File rejected as unsafe",
    scanning: "Scanning",
    readOnlyAi: "Read only",
    readWrite: "Read and edit",
    blocked: "Actions unavailable for now",
    gitStatus: "File status",
    gitConflict: "Local changes conflict with the current version",
    secureRedaction: "Verified protected copy",
    redactions: "Protected copies",
    noRedactions: "No protected copies",
    redactionDownloadFailed: "Could not download protected copy",
    redactionRegions: "Protected areas: {{count}}",
    redactionStates: {
      pending: "Awaiting verification",
      verifying: "Verifying",
      verified: "Verified",
      rejected: "Rejected",
    },
    officeLifecycle: "Protected editor",
    officeOpening: "Preparing editor",
    officeEditorLoading: "Loading editor",
    officeEditorReady: "Protected editor ready",
    officeEditorError: "Could not open protected editor",
    officeEditorAvailable: "Choose Edit to open securely.",
    officeEditorErrorAction: "Editor failed to load. Try again or close it.",
    officeFinalizeError: "The previous editor session is still closing.",
    officeFinalizeRetryAction: "Editor closed before saving. Retry closing it.",
    officeFinalizeStalled: "Try again shortly.",
    officeFinalizing: "Saving changes",
    officeRevoked: "Editor access revoked",
    officeSaveFailed: "Could not save changes",
    officeResetting: "Closing session",
    officeResetError: "Could not close this session. Retry.",
    officeResetAction: "Close session and discard unsaved changes",
    editorStates: {
      opening: "Opening editor",
      active: "Protected editor ready",
      saving: "Saving",
      finalizing: "Saving changes",
      closed: "Editor closed",
      save_failed: "Save failed",
      authorization_revoked: "Editor access revoked",
    },
    outputs: "Results",
    noOutputs: "No prepared results",
    retry: "Retry check",
    close: "Close",
    ingestStates: {
      uploading: "Uploading",
      quarantined: "Awaiting security check",
      scanning: "Scanning",
      ready: "Ready",
      rejected_unsafe: "Unsafe",
      failed_transient: "Check failed",
    },
    sourceTypes: {
      upload: "Upload",
      office: "Office editor",
      markdown: "Source",
      proposal: "Suggested change",
      git: "Repository",
      recovery: "Recovery",
    },
  },
  proposals: {
    title: "Proposals",
    blocked: "Blocked by an edit session",
    conflict: "Conflicts with the current version",
    regenerate: "Regenerate against version {{version}}",
    pending: "Awaiting a decision",
  },
  errors: {
    authRequired: "Sign-in required",
    staleRevision: "This object has changed",
    staleBoardRevision: "This board has changed",
    forbidden: "Insufficient permission",
    invalidRequest: "Invalid request",
    routeRejected: "Route rejected",
    mixedCredentials: "Credential classes cannot be mixed",
    invalidOrigin: "Request origin rejected",
    browserCookieRejected: "Browser sessions are not accepted here",
    deviceGrantRequired: "Device grant required",
    internalRouteRejected: "Internal route unavailable",
    searchFailed: "Search failed",
    connectorOffline: "Connection unavailable",
    saveConflict: "The saved copy conflicts with the current version",
    editSessionActive: "The document is open in the editor",
    generic: "An error occurred",
  },
  legal: {
    notice: "AGPL-3.0-only · No warranty · Source code",
  },
} as const satisfies TranslationShape<typeof ru>;
